import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@constants';
import { authService } from '@services';
import type { WalletType } from '@types';

// Helper function to detect network errors
const getNetworkErrorMessage = (error: any): string | null => {
  // CORS error
  if (error?.message?.includes('CORS') || error?.code === 'ERR_NETWORK') {
    return 'Network connection is unstable. Please try again.';
  }

  // Network timeout or connection refused
  if (error?.code === 'ECONNREFUSED' || error?.message?.includes('timeout')) {
    return 'Unable to connect to server. Please check your network connection.';
  }

  // Generic network error
  if (!error?.response && error?.request) {
    return 'Network connection error. Please try again later.';
  }

  return null;
};

interface User {
  id: string;
  address: string;
  username?: string;
  level: number;
  experience: number;
  totalCheckIn: number;
  coins: number;
  energy?: number;
  walletType?: WalletType;
  isActive?: boolean;
  // User assets from API (matching API response structure)
  sei?: number;
  gold?: number;
  scores?: number;
  pvpRankScore?: number;
}

interface AuthState {
  // SEI Backend Auth
  user: User | null;
  accessToken: string | null;
  refreshTokenValue: string | null;
  expiresAt: number | null;

  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  networkError: string | null;
  hasHydrated: boolean;

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  refreshToken: () => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  clearError: () => void;
  clearNetworkError: () => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  validateAndUpdateUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // SEI Backend Auth
      user: null,
      accessToken: null,
      refreshTokenValue: null,
      expiresAt: null,

      // State
      isAuthenticated: false,
      isLoading: false,
      error: null,
      networkError: null,
      hasHydrated: false,

      setAuth: (user, accessToken, refreshToken) => {
        const expiresAt = Date.now() + 86400 * 1000; // 24 hours

        set({
          user,
          accessToken,
          refreshTokenValue: refreshToken,
          expiresAt,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          networkError: null,
        });

        // Send access token to Unity
        console.log('[authStore] Dispatching access token to Unity');
        window.dispatchEvent(
          new CustomEvent('sei-unity-token-update', {
            detail: { token: accessToken },
          })
        );
      },

      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshTokenValue: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          networkError: null,
        });

        // Send empty token to Unity
        console.log('[authStore] Dispatching empty token to Unity on logout');
        window.dispatchEvent(
          new CustomEvent('sei-unity-token-update', {
            detail: { token: '' },
          })
        );
      },

      setLoading: (loading) => set({ isLoading: loading }),

      refreshToken: async () => {
        const { refreshTokenValue } = get();
        if (!refreshTokenValue) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await authService.refreshToken(refreshTokenValue);
          const expiresAt = Date.now() + parseInt(response.expiresIn) * 1000;

          set({
            accessToken: response.accessToken,
            refreshTokenValue: response.refreshToken,
            expiresAt,
          });

          // Send new access token to Unity after refresh
          console.log(
            '[authStore] Dispatching refreshed access token to Unity'
          );
          window.dispatchEvent(
            new CustomEvent('sei-unity-token-update', {
              detail: { token: response.accessToken },
            })
          );
        } catch (error) {
          get().logout().catch(console.error);
          throw error;
        }
      },

      logout: async () => {
        const { refreshTokenValue } = get();

        // Call backend logout API if we have refresh token
        if (refreshTokenValue) {
          try {
            await authService.logout(refreshTokenValue);
          } catch (error) {
            console.error('Backend logout failed:', error);
            // Continue with local logout even if backend call fails
          }
        }

        // Clear local state
        get().clearAuth();
      },

      updateUser: (updates) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      clearError: () => set({ error: null }),

      clearNetworkError: () => set({ networkError: null }),

      validateAndUpdateUser: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          get().logout();
          return;
        }

        try {
          const userProfile = await authService.getUserProfile();

          // Map API response to User interface
          const updatedUser: User = {
            id: userProfile.id,
            address: userProfile.walletAddress,
            username: userProfile.id,
            level: 1,
            experience: 0,
            coins: userProfile.userAssets.gold || 0,
            energy: userProfile.energy,
            walletType: userProfile.walletType as WalletType,
            isActive: userProfile.isActive,
            sei: userProfile.userAssets.sei || 0,
            gold: userProfile.userAssets.gold,
            scores: userProfile.userAssets.scores,
            pvpRankScore: userProfile.userAssets.pvpRankScore || 0,
            totalCheckIn: userProfile.totalCheckIn || 0,
          };

          set({ user: updatedUser });
        } catch (error: any) {
          console.error('Token validation failed:', error);

          // Check if it's a network error
          const networkError = getNetworkErrorMessage(error);
          if (networkError) {
            set({ networkError });
            throw error;
          }

          // If 401, the API interceptor already tried refresh token
          // If we get here, both access & refresh tokens are invalid
          if (error?.response?.status === 401) {
            console.log('Authentication expired, logging out user');
            get().logout().catch(console.error);
          }
          // If 404, user doesn't exist in database - logout completely
          if (error?.response?.status === 404) {
            console.log('User not found in database, logging out completely');
            window.dispatchEvent(new CustomEvent('sei-user-deleted'));
            get().logout().catch(console.error);
          }
          // Clear loading state on any error
          set({ isLoading: false });
          throw error;
        }
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_TOKEN,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshTokenValue: state.refreshTokenValue,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Set hasHydrated to true when rehydration completes
        if (state) {
          state.hasHydrated = true;
        }
      },
    }
  )
);
